import { Injectable } from '@angular/core';
import { ParamMap } from '@angular/router';
import {
  from, switchMap, take,
} from 'rxjs';

import { IrisModalService, IrisModalSize } from '@sondermind/iris/modal';
import { SegmentService } from '@sondermind/segment';
import { USStateOptions } from '@sondermind/constants';

import { QuickCheckoutComponent } from '../components/quick-checkout.component';
import { IRequestASessionClick } from '../models/request-a-session-click.interface';
import { RequestASessionClickTarget } from '../models/enums/request-a-session-click-target.enum';
import { IRequestSessionParams } from '../models/request-session-params.interface';
import { IQuickCheckoutModalConfigData } from '../models/quick-checkout-modal-config.model';
import {
  IPublicRequestSessionSegmentSchema,
  IPublicRequestSessionClickData, PublicRequestSessionSegmentEvents
} from '../models/segment/public-request-session-segment-schema.interface';
import { RequestSessionExitClickTarget } from '../models/enums/request-session-exits.enum';

enum LandingPageHeaderParam {
  ANON_USER_ID = 'anonUserId',
  NAME = 'name',
  PAYMENT_PREF = 'paymentPref',
  PAYMENT_TAG = 'paymentTag',
  SHARE_CODE = 'share_code',
  STATE = 'state',
  REFERRING_URL = 'referringUrl'
}
const PSL_MATCHFLOW_SLUG = 'shareable-link-match-flow';

@Injectable({ providedIn: 'root' })
export class RequestASessionService {
  constructor(
    private irisModalService: IrisModalService,
    private segmentService: SegmentService<IPublicRequestSessionSegmentSchema>
  ) {}

  onRequestSessionButtonClicked(click: IRequestASessionClick): void {
    if (click.clickTarget === RequestASessionClickTarget.LINK_TO_MATCH_FLOW) {
      const clickData: IPublicRequestSessionClickData = {
        accepting: click.profile.accepting_clients,
        flow: PSL_MATCHFLOW_SLUG,
        personaProviderId: click.profile.id,
      };

      from(this.segmentService.track<PublicRequestSessionSegmentEvents.PUBLIC_REQUEST_SESSION_CLICKED>(
        PublicRequestSessionSegmentEvents.PUBLIC_REQUEST_SESSION_CLICKED,
        clickData
      )).pipe(take(1)).subscribe();
    } else {
      const clickData: IPublicRequestSessionClickData = {
        accepting: click.profile.accepting_clients,
        flow: '',
        personaProviderId: click.profile.id,
      };

      from(this.segmentService.track<PublicRequestSessionSegmentEvents.PUBLIC_REQUEST_SESSION_CLICKED>(
        PublicRequestSessionSegmentEvents.PUBLIC_REQUEST_SESSION_CLICKED,
        clickData
      )).pipe(take(1)).subscribe();

      this.openRequestASessionModal(click);
    }
  }

  private openRequestASessionModal(click: IRequestASessionClick): void {
    const queryParamStateAbbrev = USStateOptions.find((state) => state.label === click.queryParams.state)?.value;
    const quickCheckoutInputData = {
      providerCredentialedStates: click.profile.credentialed_states.map((state) => state.name),
      initialState: queryParamStateAbbrev || click.profile.practice_state.abbrev,
      providerAcceptedInsuranceIds: click.profile.provider_network_company_ids,
      providerId: click.profile.id,
      providerFirstName: click.profile.display_first_name,
      providerTimeZone: click.profile.time_zone,
      requestedTimeSlot: null // TODO: When calendar is hooked up, pass it in here
    };

    const irisModalRef = this.irisModalService.open<QuickCheckoutComponent, IQuickCheckoutModalConfigData, string>(
      QuickCheckoutComponent,
      {
        size: IrisModalSize.MD,
        data: quickCheckoutInputData,
      }
    );

    irisModalRef.afterClosed$().pipe(
      take(1),
      switchMap(() => from(this.segmentService.track<PublicRequestSessionSegmentEvents.PSL_QUICK_CHECKOUT_EXITED>(
        PublicRequestSessionSegmentEvents.PSL_QUICK_CHECKOUT_EXITED,
        { exitedBy: RequestSessionExitClickTarget.EXIT_X_BUTTON })
      )),
    ).subscribe();
  }

  getNameFromParams(routeParams: ParamMap): string {
    return routeParams.get(LandingPageHeaderParam.NAME) ?? '';
  }

  getShareCodeFromParams(routeParams: ParamMap): string {
    return routeParams.get(LandingPageHeaderParam.SHARE_CODE) ?? '';
  }

  parseQueryParams(queryParams: ParamMap): IRequestSessionParams {
    const incomingState: string | null = queryParams.get(LandingPageHeaderParam.STATE);
    const incomingPaymentPreference: string | null = queryParams.get(LandingPageHeaderParam.PAYMENT_PREF);
    const incomingInsuranceTag: string | null = queryParams.get(LandingPageHeaderParam.PAYMENT_TAG);

    let requestSessionQueryParams: IRequestSessionParams = {
      anonUserId: queryParams.get(LandingPageHeaderParam.ANON_USER_ID) ?? '',
      referringUrl: queryParams.get(LandingPageHeaderParam.REFERRING_URL)
        ?? encodeURIComponent(window.location.href),
    };
    if (incomingState) {
      requestSessionQueryParams = { ...requestSessionQueryParams, state: incomingState };
    }
    if (incomingPaymentPreference) {
      requestSessionQueryParams = { ...requestSessionQueryParams, paymentPref: incomingPaymentPreference };
    }
    if (incomingInsuranceTag) {
      requestSessionQueryParams = { ...requestSessionQueryParams, paymentTag: incomingInsuranceTag };
    }

    return requestSessionQueryParams;
  }
}
